import { useState } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { DrillDownModal } from './DrillDownModal'

interface KpiCardProps {
  label: string
  value: string | number
  icon: LucideIcon
  color: string
  trend?: { value: number; label: string }
  sparkline?: React.ReactNode
  loading?: boolean
  drillDown?: {
    title: string
    subtitle?: string
    chart?: React.ReactNode
    table?: React.ReactNode
    actions?: Array<{ label: string; onClick: () => void; variant?: 'primary' | 'secondary' | 'ghost' }>
  }
}

export function KpiCard({ label, value, icon: Icon, color, trend, sparkline, loading, drillDown }: KpiCardProps) {
  const [open, setOpen] = useState(false)
  const isClickable = !!drillDown

  return (
    <>
      <button
        onClick={() => isClickable && setOpen(true)}
        disabled={!isClickable}
        aria-label={`Open detail for ${label}`}
        className={cn(
          'bg-white rounded-card border border-ink-border p-5 text-left w-full transition-all duration-150',
          isClickable && 'cursor-pointer hover:shadow-card hover:ring-1 hover:ring-gold active:scale-[0.98]',
          !isClickable && 'cursor-default',
        )}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-3">
              <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', color)}>
                <Icon size={20} />
              </div>
              {sparkline && <div className="flex-1 min-w-0">{sparkline}</div>}
            </div>
            {loading ? (
              <div className="h-7 w-20 bg-paper-warm rounded animate-pulse" />
            ) : (
              <p className="font-serif text-2xl font-bold text-ink tracking-tight truncate">{value}</p>
            )}
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-ink-muted">{label}</p>
              {trend && (
                <span className={cn(
                  'text-xs font-semibold px-1.5 py-0.5 rounded',
                  trend.value >= 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-700 bg-red-50',
                )}>
                  {trend.value >= 0 ? '▲' : '▼'} {Math.abs(trend.value).toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        </div>
      </button>

      {drillDown && (
        <DrillDownModal
          open={open}
          onClose={() => setOpen(false)}
          title={drillDown.title}
          subtitle={drillDown.subtitle}
          chart={drillDown.chart}
          table={drillDown.table}
          actions={drillDown.actions}
        />
      )}
    </>
  )
}
